import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatTabGroup } from '@angular/material/tabs';
import { WebsiteStateService } from '../../../../../state-management/services/website-state.service';
import { QuickActionItem, WidgetConfiguration } from '../../../../../state-management/model/interfaces/state.model';
import { WebsiteConfigFormExpansionPanelComponent } from '../website-config-form-expansion-panel/website-config-form-expansion-panel.component';

@Component({
  selector: 'app-widget-quick-action',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    DragDropModule,
    WebsiteConfigFormExpansionPanelComponent
  ],
  templateUrl: './widget-quick-action.component.html',
  styleUrl: './widget-quick-action.component.scss'
})
export class WidgetQuickActionComponent implements OnInit {
  form!: FormGroup;
  private fb = inject(FormBuilder);
  private websiteState = inject(WebsiteStateService);
  private tabGroup = inject(MatTabGroup, { optional: true });
  private snackBar = inject(MatSnackBar);

  ngOnInit(): void {
    this.initializeForm();
    this.loadCurrentConfiguration();
    this.setupFormSubscription();
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      isEnabled: [true],
      position: ['bottom-right'],
      items: this.fb.array([])
    });
  }

  private loadCurrentConfiguration(): void {
    const currentConfig = this.websiteState.configuration().widgets;
    
    this.form.patchValue({
      isEnabled: currentConfig.quickActions.isEnabled,
      position: currentConfig.quickActions.position
    });

    // Clear existing items and add current ones
    this.itemsArray.clear();
    currentConfig.quickActions.items
      .sort((a: QuickActionItem, b: QuickActionItem) => a.order - b.order)
      .forEach((item: QuickActionItem) => this.addItemToForm(item));
  }

  private addItemToForm(item: QuickActionItem): void {
    const itemForm = this.fb.group({
      id: [item.id],
      type: [item.type],
      isActive: [item.isActive],
      order: [item.order],
      label: [item.label || ''],
      sourceInfo: [item.sourceInfo || '']
    });

    this.itemsArray.push(itemForm);
  }

  private setupFormSubscription(): void {
    this.form.valueChanges.subscribe(() => {
      this.saveConfiguration();
    });
  }

  private saveConfiguration(): void {
    if (this.form.valid) {
      const formValue = this.form.value;
      
      // Update order based on current array position
      const updatedItems = formValue.items.map((item: any, index: number) => ({
        ...item,
        order: index + 1
      }));

      const widgetConfig: WidgetConfiguration = {
        quickActions: {
          isEnabled: formValue.isEnabled,
          position: formValue.position,
          items: updatedItems
        }
      };

      // Update the website state
      this.websiteState.updateConfiguration({ widgets: widgetConfig });
    }
  }

  get itemsArray(): FormArray {
    return this.form.get('items') as FormArray;
  }

  onDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      const itemsArray = this.itemsArray;
      const draggedItem = itemsArray.at(event.previousIndex);
      const draggedType = draggedItem?.get('type')?.value;
      
      // Prevent dropping fixed position items
      if (!this.canDragItem(draggedType)) {
        return;
      }
      
      // Prevent dropping into fixed position slots
      const targetItem = itemsArray.at(event.currentIndex);
      const targetType = targetItem?.get('type')?.value;
      if (!this.canDragItem(targetType)) {
        return;
      }
      
      itemsArray.removeAt(event.previousIndex);
      itemsArray.insert(event.currentIndex, draggedItem);
      
      // Update order values while preserving fixed positions
      this.updateItemOrders();
    }
  }

  private updateItemOrders(): void {
    this.itemsArray.controls.forEach((control, index) => {
      const type = control.get('type')?.value;
      let order: number;
      
      // Fixed positions for specific types
      if (type === 'social') {
        order = 1000; // Always last
      } else if (type === 'contact-form') {
        order = 998; // Second to last group
      } else if (type === 'directions') {
        order = 999; // Second to last group
      } else {
        // Dynamic order for draggable items (1-997)
        const draggableIndex = this.itemsArray.controls
          .slice(0, index)
          .filter(c => this.canDragItem(c.get('type')?.value))
          .length;
        order = draggableIndex + 1;
      }
      
      control.patchValue({ order });
    });
  }

  getActionDisplayName(type: string): string {
    const names: Record<string, string> = {
      'phone': 'Telefon anrufen',
      'email': 'E-Mail senden',
      'whatsapp': 'WhatsApp Chat',
      'order': 'Bestellung aufgeben',
      'contact-form': 'Kontaktformular',
      'directions': 'Wegbeschreibung',
      'social': 'Social Media Links'
    };
    return names[type] || type;
  }

  getActionDescription(type: string): string {
    const config = this.websiteState.configuration();

    switch (type) {
      case 'phone':
        return config.contactData.phone || config.contactData.mobile || 'Keine Telefonnummer konfiguriert';
      case 'email':
        return config.contactData.email || 'Keine E-Mail konfiguriert';
      case 'whatsapp':
        const whatsappLink = config.socialMediaLinks.find((link: any) => 
          link.platform.toLowerCase() === 'whatsapp' && link.isActive
        );
        return whatsappLink?.url || 'Keine WhatsApp-Nummer in Social Media konfiguriert';
      case 'directions':
        const address = `${config.contactData.street || ''}, ${config.contactData.city || ''}`.trim();
        return address || 'Keine Adresse konfiguriert';
      case 'social':
        const activeLinks = config.socialMediaLinks?.filter((link: any) => 
          link.isActive && link.platform !== 'whatsapp'
        ) || [];
        if (activeLinks.length === 1) {
          return '1 aktiver Link';
        } else if (activeLinks.length > 1) {
          return `${activeLinks.length} aktive Links`;
        } else {
          return 'Keine Social Media Links konfiguriert';
        }
      case 'order':
        return 'Bestellformular';
      case 'contact-form':
        return 'Kontaktformular';
      default:
        return '';
    }
  }

  getConfigurationTab(type: string): string | null {
    const tabs: Record<string, string> = {
      'phone': 'Kontaktdaten',
      'email': 'Kontaktdaten',
      'whatsapp': 'Social Media & Partner',
      'directions': 'Kontaktdaten',
      'social': 'Social Media & Partner'
    };
    return tabs[type] || null;
  }

  navigateToConfiguration(type: string): void {
    const tabName = this.getConfigurationTab(type);
    if (tabName && this.tabGroup) {
      // Find the tab index by label
      const tabIndex = this.getTabIndexByLabel(tabName);
      if (tabIndex !== -1) {
        this.tabGroup.selectedIndex = tabIndex;
      }
    }
  }

  private getTabIndexByLabel(label: string): number {
    if (!this.tabGroup) return -1;
    
    const tabs = this.tabGroup._tabs.toArray();
    return tabs.findIndex(tab => tab.textLabel === label);
  }

  canDragItem(type: string): boolean {
    // Social media, contact-form and directions cannot be dragged as they have fixed positions
    const fixedPositionItems = ['social', 'contact-form', 'directions'];
    return !fixedPositionItems.includes(type);
  }

  hasRequiredData(type: string): boolean {
    const config = this.websiteState.configuration();
    
    switch (type) {
      case 'phone':
        return !!config.contactData.phone;
      case 'email':
        return !!config.contactData.email;
      case 'whatsapp':
        // WhatsApp should come from Social Media Links
        return config.socialMediaLinks?.some((link: any) => 
          link.platform === 'whatsapp' && link.isActive && link.url
        ) || false;
      case 'directions':
        return !!(config.contactData.street && config.contactData.city);
      case 'social':
        return config.socialMediaLinks?.some((link: any) => 
          link.isActive && link.url && link.platform !== 'whatsapp'
        ) || false;
      case 'order':
      case 'contact-form':
        return true; // Static functionality, always available
      default:
        return false;
    }
  }

  showDataSourceInfo(type: string): void {
    const dataSourceInfo = this.getDataSourceInfo(type);
    this.snackBar.open(dataSourceInfo, 'Schließen', {
      duration: 8000,
      panelClass: ['info-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  private getDataSourceInfo(type: string): string {
    switch (type) {
      case 'phone':
        return 'Wird bearbeitet über Reiter "Kontaktdaten" → Unternehmensdaten. Priorität: 1) Telefon, 2) Handy (falls Telefon nicht angegeben).';
      case 'email':
        return 'Wird bearbeitet über Reiter "Kontaktdaten" → Unternehmensdaten → E-Mail Adresse.';
      case 'whatsapp':
        return 'Wird bearbeitet über Reiter "Social Media & Partner" → Social Media Links → WhatsApp (muss aktiviert sein).';
      case 'directions':
        return 'Wird bearbeitet über Reiter "Kontaktdaten" → Unternehmensdaten → Straße und Stadt werden für die Wegbeschreibung verwendet.';
      case 'social':
        return 'Wird bearbeitet über Reiter "Social Media & Partner" → Social Media Links → Alle aktivierten Links (außer WhatsApp, da separat angezeigt).';
      case 'order':
        return 'Öffnet das Bestellformular.';
      case 'contact-form':
        return 'Öffnet das Kontaktformular.';
      default:
        return 'Keine Informationen zur Datenquelle verfügbar.';
    }
  }
}
